import { Calendar as CalendarApp } from '@datapad/calendar';
import {
	CalendarDate,
	getMonthFromDate,
	type MonthOfYear,
	sithEmpireClaimsHuttSpace,
	useAppContext,
} from '@datapad/data-model';
import { assertNotUndefined } from '@datapad/utilities';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { DatapadAppStateContext } from '../../../utilities';
import { AppId } from '../AppId';

const defaultMonth = sithEmpireClaimsHuttSpace.getMonth();

/**
 * Calendar app wrapper.
 *
 * @remarks
 * Handles URL parameter management for month selection.
 * URL format: `/calendar?month=<year>-<month-or-festival-name>`
 * @example `/calendar?month=5-Elona`, `/calendar?month=5-Festival-of-Life`
 */
export function Calendar(): React.ReactElement {
	const location = useLocation();
	const appContext = useAppContext();

	const { changeAppSelection } = assertNotUndefined(React.useContext(DatapadAppStateContext));

	// Get current campaign date if available (respects "View As" mode)
	// When viewing as guest, don't use campaign date since guests don't have characters/campaigns
	const effectivePermissionLevel = appContext.getEffectivePermissionLevel();
	const campaignDate =
		effectivePermissionLevel !== 'guest' &&
		'campaign' in appContext &&
		appContext.campaign !== undefined
			? appContext.getCampaign(appContext.campaign).currentDate
			: undefined;

	// Parse URL for selected month
	const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location]);

	// Check if URL already has date params
	const urlDate = React.useMemo(() => CalendarDate.parseUrlSearch(searchParams), [searchParams]);

	// Determine selected month: from URL, from current date, or default
	const selectedMonth: MonthOfYear = React.useMemo(() => {
		if (urlDate) {
			console.log('Loading date from URL.');
			return urlDate.getMonth();
		}
		if (campaignDate) {
			console.log('Loading date from campaign.');
			return getMonthFromDate(campaignDate);
		}
		console.log('Using default month');
		return defaultMonth;
	}, [urlDate, campaignDate]);

	// When defaulting to campaign date (no URL params), update URL to reflect the selection
	React.useEffect(() => {
		if (!urlDate && campaignDate) {
			const dateForMonth = CalendarDate.create(selectedMonth.year, selectedMonth.startDay);
			changeAppSelection(AppId.Calendar, dateForMonth.toUrlSearch(), undefined);
		}
	}, [urlDate, campaignDate, selectedMonth, changeAppSelection]);

	// Handle month changes by updating URL
	const handleMonthChange = React.useCallback(
		(month: MonthOfYear) => {
			// Create a CalendarDate for the first day of the month to use toUrlSearch
			const dateForMonth = CalendarDate.create(month.year, month.startDay);
			changeAppSelection(AppId.Calendar, dateForMonth.toUrlSearch(), undefined);
		},
		[changeAppSelection],
	);

	return (
		<CalendarApp
			currentDate={campaignDate}
			selectedMonth={selectedMonth}
			onMonthChange={handleMonthChange}
		/>
	);
}
